import React, { ReactNode } from 'react';
import clsx from 'clsx';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import styles from './CtaPill.module.scss';

export interface CtaPillProps {
    /** Resting label. */
    label: string;
    /** Label shown while `loading` — falls back to `label` when omitted. */
    loadingLabel?: string;
    loading?: boolean;
    disabled?: boolean;
    /** Leading icon: an image src, or a node for a colour-driven icon. Swapped for a spinner while `loading`. */
    icon: string | ReactNode;
    /** Purple is the platform default; blue marks Excel-opening CTAs. */
    tone?: 'purple' | 'blue';
    onClick?: () => void;
    /** Accessible name when the visible label alone isn't descriptive enough. */
    ariaLabel?: string;
    /** `small` is the compact 24px pill, e.g. a card-header action. Defaults to the regular size. */
    size?: 'default' | 'small';
}

/**
 * The shared pill CTA — one component behind Auditor's Comments and every
 * Excel-opening action, so they stay visually identical by construction
 * rather than by six copies of the same markup drifting apart.
 *
 * Why not extend `_custom/Button` (CLAUDE.md rule 8 wants extend before
 * build-new)? Its prop surface is close — text/icon/loading/disabled — but its
 * loading behaviour is the opposite of what this design needs: Button *hides*
 * the icon and label and overlays a centred spinner, whereas the pill swaps the
 * icon for a spinner and keeps the label visible as "Generating…". Bending that
 * would change loading semantics in a component with 137 importers to serve one
 * new visual. Kept separate, and deliberately in `_custom/` so the next pill
 * extends this rather than hand-rolling a seventh copy.
 *
 * Two styling notes, kept here because this project's SCSS carries no comments:
 * - The blue tone has no hover shift on purpose. Figma 34693-51684 specifies a
 *   single resting state and the token set has no darker partner to
 *   --connection-blue-bg; the cursor still signals interactivity. Add a token if
 *   the design gains a hover rather than inventing a shade.
 * - `.Spinner > div` zeroes LoadingSpinner's own wrapper margin, because the pill
 *   lays its children out itself.
 */
const CtaPill: React.FC<CtaPillProps> = ({
    label,
    loadingLabel,
    loading = false,
    disabled = false,
    icon,
    tone = 'purple',
    onClick,
    ariaLabel,
    size = 'default',
}) => {
    const inert = disabled || loading;

    return (
        <button
            type="button"
            className={clsx(styles.CtaPill, { [styles.Blue]: tone === 'blue', [styles.Disabled]: inert, [styles.Small]: size === 'small' })}
            onClick={() => !inert && onClick?.()}
            disabled={inert}
            // Keep the accessible name pinned to the resting label: the visible text
            // swaps to "Generating…", but a screen-reader user shouldn't see the
            // button lose its identity mid-action. aria-busy carries the state.
            aria-label={ariaLabel ?? label}
            aria-busy={loading || undefined}
        >
            {loading ? (
                <span className={styles.Spinner}>
                    <LoadingSpinner color="currentColor" height="0.875rem" />
                </span>
            ) : typeof icon === 'string' ? (
                <img src={icon} alt="" className={styles.Icon} />
            ) : (
                <span className={styles.Icon}>{icon}</span>
            )}
            {loading ? loadingLabel ?? label : label}
        </button>
    );
};

export default CtaPill;
